import {
  type PrioritizationContext,
  type StewardshipSignal,
  type TaskPrioritizationInput,
  type TaskRecommendation,
  evaluateTask,
  rankRecommendations,
  stewardshipSignal,
} from '@portfolio-pm/scoring';
import type {
  DailyCapacity,
  EnrichmentEvent,
  FieldEvidence,
  Initiative,
  ProgressEvent,
  ProtectedBlock,
  Task,
  TriageDisposition,
} from '@portfolio-pm/shared';
import {
  getDailyCapacitiesMap,
  getEnrichmentEventsMap,
  getFieldEvidenceMap,
  getInitiative,
  getInitiativesMap,
  getProgressEventsMap,
  getProtectedBlocksMap,
  getTasksMap,
  getTriageDispositionsMap,
  listDailyCapacities,
  listEnrichmentEvents,
  listFieldEvidence,
  listInitiatives,
  listProgressEvents,
  listProtectedBlocks,
  listTriageDispositions,
} from '@portfolio-pm/yjs-doc';
import type * as Y from 'yjs';
import { type Projection, createProjection } from './projection.ts';

export interface TriageCandidate {
  entityType: 'task' | 'objective' | 'initiative';
  entityId: string;
  title: string;
  reasonCodes: string[];
  sourceFingerprint: string;
  updatedAt: number;
  recommendation?: TaskRecommendation;
  stewardship?: StewardshipSignal;
}

export interface CandidatePage {
  items: TriageCandidate[];
  nextCursor?: string;
}

export interface PrioritizationProjection {
  getRevision(): number;
  getInitiative(id: string): Initiative | undefined;
  getInitiatives(): Initiative[];
  initiativesByProject(projectId: string): Initiative[];
  initiativesForObjective(objectiveId: string): Initiative[];
  tasksByInitiative(initiativeId: string): Task[];
  getDailyCapacities(): DailyCapacity[];
  getProtectedBlocks(): ProtectedBlock[];
  getFieldEvidence(): FieldEvidence[];
  getProgressEvents(): ProgressEvent[];
  getEnrichmentEvents(): EnrichmentEvent[];
  getTriageDispositions(): TriageDisposition[];
  lastMovementAt(entityType: 'objective' | 'initiative', entityId: string): number | undefined;
  stewardship(now: number): StewardshipSignal[];
  recommendations(context: PrioritizationContext): TaskRecommendation[];
  candidates(context: PrioritizationContext, limit?: number, cursor?: string): CandidatePage;
  subscribe(listener: () => void): () => void;
  destroy(): void;
}

function fingerprint(parts: unknown[]): string {
  const text = JSON.stringify(parts);
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `v1-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function candidateKey(candidate: TriageCandidate): string {
  return `${candidate.entityType}:${candidate.updatedAt.toString().padStart(16, '0')}:${candidate.entityId}`;
}

export function createPrioritizationProjection(
  doc: Y.Doc,
  baseProjection?: Projection,
): PrioritizationProjection {
  const base = baseProjection ?? createProjection(doc);
  const ownsBase = baseProjection === undefined;
  const listeners = new Set<() => void>();
  let destroyed = false;
  let revision = 0;

  const notify = (): void => {
    if (destroyed) return;
    revision += 1;
    for (const listener of listeners) listener();
  };
  const observedMaps = [
    getInitiativesMap(doc),
    getDailyCapacitiesMap(doc),
    getFieldEvidenceMap(doc),
    getEnrichmentEventsMap(doc),
    getTasksMap(doc),
    getProgressEventsMap(doc),
    getProtectedBlocksMap(doc),
    getTriageDispositionsMap(doc),
  ];
  for (const map of observedMaps) map.observeDeep(notify);
  const unsubscribeBase = base.subscribe(notify);

  const lastMovementAt = (
    entityType: 'objective' | 'initiative',
    entityId: string,
  ): number | undefined => {
    const evidence: number[] = listProgressEvents(doc)
      .filter((event) => event.entityType === entityType && event.entityId === entityId)
      .map((event) => event.occurredAt);

    if (entityType === 'objective') {
      const keyResultIds = new Set(base.keyResultsByObjective(entityId).map((result) => result.id));
      const advancingCheckIns = base
        .getCheckIns()
        .filter(
          (checkIn) =>
            checkIn.progressSnapshot !== undefined &&
            ((checkIn.targetType === 'objective' && checkIn.targetId === entityId) ||
              (checkIn.targetType === 'key_result' && keyResultIds.has(checkIn.targetId))),
        )
        .sort((a, b) => a.createdAt - b.createdAt);
      const priorProgress = new Map<string, number>();
      for (const checkIn of advancingCheckIns) {
        const key = `${checkIn.targetType}:${checkIn.targetId}`;
        const previous = priorProgress.get(key);
        if (previous === undefined || (checkIn.progressSnapshot as number) > previous) {
          evidence.push(checkIn.createdAt);
        }
        priorProgress.set(key, checkIn.progressSnapshot as number);
      }
      evidence.push(
        ...base
          .getTasks()
          .filter(
            (task) =>
              task.completedAt !== undefined &&
              (task.objectiveId === entityId || (task.krId && keyResultIds.has(task.krId))),
          )
          .map((task) => task.completedAt as number),
      );
    } else {
      evidence.push(
        ...base
          .getTasks()
          .filter((task) => task.initiativeId === entityId && task.completedAt !== undefined)
          .map((task) => task.completedAt as number),
      );
    }
    return evidence.length ? Math.max(...evidence) : undefined;
  };

  const stewardship = (now: number): StewardshipSignal[] => {
    const objectives = base
      .getObjectives()
      .map((objective) =>
        stewardshipSignal(objective, 'objective', lastMovementAt('objective', objective.id), now),
      );
    const initiatives = listInitiatives(doc).map((initiative) =>
      stewardshipSignal(initiative, 'initiative', lastMovementAt('initiative', initiative.id), now),
    );
    return [...objectives, ...initiatives].sort(
      (a, b) =>
        Number(b.stalled) - Number(a.stalled) ||
        b.missedCadenceIntervals - a.missedCadenceIntervals ||
        a.title.localeCompare(b.title) ||
        a.entityId.localeCompare(b.entityId),
    );
  };

  const recommendations = (context: PrioritizationContext): TaskRecommendation[] => {
    const tasks = base.getTasks();
    const tasksById = new Map(tasks.map((task) => [task.id, task]));
    const initiatives = new Map(
      listInitiatives(doc).map((initiative) => [initiative.id, initiative]),
    );
    const objectives = new Map(base.getObjectives().map((objective) => [objective.id, objective]));
    const stewardshipById = new Map(
      stewardship(context.now).map((signal) => [`${signal.entityType}:${signal.entityId}`, signal]),
    );
    const inputs: TaskPrioritizationInput[] = tasks.map((task) => {
      const initiative = task.initiativeId ? initiatives.get(task.initiativeId) : undefined;
      const objective = task.objectiveId ? objectives.get(task.objectiveId) : undefined;
      const signals = [
        objective ? stewardshipById.get(`objective:${objective.id}`) : undefined,
        initiative ? stewardshipById.get(`initiative:${initiative.id}`) : undefined,
      ].filter((signal): signal is StewardshipSignal => signal !== undefined);
      return {
        task,
        tasksById,
        ...(objective ? { objective } : {}),
        ...(initiative ? { initiative } : {}),
        stewardship: signals,
      };
    });
    return rankRecommendations(inputs.map((input) => evaluateTask(input, context)));
  };

  const candidates = (
    context: PrioritizationContext,
    limit = 25,
    cursor?: string,
  ): CandidatePage => {
    const recommendationCandidates: TriageCandidate[] = recommendations(context)
      .filter(
        (recommendation) =>
          recommendation.eligibility === 'provisional' ||
          recommendation.eligibility === 'needs_clarification' ||
          recommendation.eligibility === 'oversized' ||
          (recommendation.eligibility === 'waiting' &&
            recommendation.reasonCodes.includes('missing_waiting_trigger')),
      )
      .map((recommendation) => ({
        entityType: 'task',
        entityId: recommendation.task.id,
        title: recommendation.task.title,
        reasonCodes: recommendation.reasonCodes,
        sourceFingerprint: fingerprint([
          recommendation.task.updatedAt,
          recommendation.reasonCodes,
          recommendation.inputs,
        ]),
        updatedAt: recommendation.task.updatedAt,
        recommendation,
      }));
    const stewardshipCandidates: TriageCandidate[] = stewardship(context.now)
      .filter((signal) => signal.stalled || !signal.completeness.complete)
      .map((signal) => {
        const entity =
          signal.entityType === 'objective'
            ? base.getObjective(signal.entityId)
            : getInitiative(doc, signal.entityId);
        return {
          entityType: signal.entityType,
          entityId: signal.entityId,
          title: signal.title,
          reasonCodes: signal.reasonCodes,
          sourceFingerprint: fingerprint([
            entity?.updatedAt,
            signal.reasonCodes,
            signal.lastMovementAt,
          ]),
          updatedAt: entity?.updatedAt ?? 0,
          stewardship: signal,
        };
      });
    const dispositions = new Map(
      listTriageDispositions(doc).map((item) => [`${item.entityType}:${item.entityId}`, item]),
    );
    const sorted = [...recommendationCandidates, ...stewardshipCandidates]
      .filter((candidate) => {
        const disposition = dispositions.get(`${candidate.entityType}:${candidate.entityId}`);
        if (!disposition) return true;
        if (disposition.sourceFingerprint !== candidate.sourceFingerprint) return true;
        return disposition.resurfaceAt !== undefined && disposition.resurfaceAt <= context.now;
      })
      .sort((a, b) => candidateKey(a).localeCompare(candidateKey(b)));
    const cursorIndex = cursor ? sorted.findIndex((item) => candidateKey(item) > cursor) : 0;
    const start = cursorIndex < 0 ? sorted.length : cursorIndex;
    const safeLimit = Math.max(1, Math.min(100, Math.floor(limit)));
    const items = sorted.slice(start, start + safeLimit);
    const last = items.at(-1);
    return {
      items,
      ...(start + items.length < sorted.length && last ? { nextCursor: candidateKey(last) } : {}),
    };
  };

  return {
    getRevision: () => revision,
    getInitiative: (id) => getInitiative(doc, id),
    getInitiatives: () => listInitiatives(doc),
    initiativesByProject: (projectId) =>
      listInitiatives(doc).filter((initiative) => initiative.projectId === projectId),
    initiativesForObjective: (objectiveId) =>
      listInitiatives(doc).filter((initiative) => initiative.objectiveIds.includes(objectiveId)),
    tasksByInitiative: (initiativeId) =>
      base.getTasks().filter((task) => task.initiativeId === initiativeId),
    getDailyCapacities: () => listDailyCapacities(doc),
    getProtectedBlocks: () => listProtectedBlocks(doc),
    getFieldEvidence: () => listFieldEvidence(doc),
    getProgressEvents: () => listProgressEvents(doc),
    getEnrichmentEvents: () => listEnrichmentEvents(doc),
    getTriageDispositions: () => listTriageDispositions(doc),
    lastMovementAt,
    stewardship,
    recommendations,
    candidates,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      unsubscribeBase();
      for (const map of observedMaps) map.unobserveDeep(notify);
      if (ownsBase) base.destroy();
      listeners.clear();
    },
  };
}
